import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import Icon from '../../docs/Icon'

export type DropdownMenuItem = {
  /** The item's visible name. */
  label: string
  /** Runs when the item is chosen; the menu then closes and focus returns to the button. */
  onSelect: () => void
  /** Shown but not choosable. */
  disabled?: boolean
}

export type DropdownMenuProps = {
  /** The button's visible name, such as "Actions". */
  label: string
  items: DropdownMenuItem[]
  /** Which edge of the button the menu lines up with. */
  align?: 'start' | 'end'
}

/**
 * A button that opens a short list of actions. Follows the WAI-ARIA menu
 * button pattern: Enter, Space and the arrow keys open it, arrows, Home and End
 * move between items, typing a letter jumps to the next item starting with it,
 * and Escape or a click outside closes it.
 */
export default function DropdownMenu({ label, items, align = 'start' }: DropdownMenuProps) {
  const buttonId = useId()
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    if (!open) return
    itemRefs.current[focusIndex]?.focus()
  }, [open, focusIndex])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  const openAt = (index: number) => {
    setFocusIndex(index)
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    button.current?.focus()
  }

  const choose = (item: DropdownMenuItem) => {
    if (item.disabled) return
    close()
    item.onSelect()
  }

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') openAt(0)
    else if (event.key === 'ArrowUp') openAt(items.length - 1)
    else return
    event.preventDefault()
  }

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const last = items.length - 1
    if (event.key === 'ArrowDown') setFocusIndex(focusIndex === last ? 0 : focusIndex + 1)
    else if (event.key === 'ArrowUp') setFocusIndex(focusIndex === 0 ? last : focusIndex - 1)
    else if (event.key === 'Home') setFocusIndex(0)
    else if (event.key === 'End') setFocusIndex(last)
    else if (event.key === 'Escape') close()
    else if (event.key === 'Tab') {
      // Tab leaves the menu, so it closes and focus moves on as usual.
      setOpen(false)
      return
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      const letter = event.key.toLowerCase()
      const order = [...items.slice(focusIndex + 1), ...items.slice(0, focusIndex + 1)]
      const match = order.find((item) => item.label.toLowerCase().startsWith(letter))
      if (!match) return
      setFocusIndex(items.indexOf(match))
    } else return
    event.preventDefault()
  }

  return (
    <div ref={root} className={`mds-dropdown mds-dropdown--${align}`}>
      <button
        ref={button}
        id={buttonId}
        type="button"
        className="mds-button mds-button--secondary mds-dropdown__button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? setOpen(false) : openAt(0))}
        onKeyDown={onButtonKeyDown}
      >
        {label}
        <Icon name="chevron" size={16} className="mds-dropdown__chevron" />
      </button>
      <div id={menuId} role="menu" aria-labelledby={buttonId} className="mds-dropdown__menu" hidden={!open} onKeyDown={onMenuKeyDown}>
        {items.map((item, index) => (
          <button
            key={item.label}
            ref={(node) => {
              itemRefs.current[index] = node
            }}
            type="button"
            role="menuitem"
            tabIndex={-1}
            aria-disabled={item.disabled || undefined}
            className="mds-dropdown__item"
            onClick={() => choose(item)}
            onPointerMove={() => !item.disabled && setFocusIndex(index)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  )
}
